import axios from 'axios';
import i18next from 'i18next';
import { Category } from 'src/models';
import { getRootStoreSingleton } from 'src/models/root-store/root-store-singleton';

import { DEFAULT_AXIOS_CONFIG } from './axiosConfig';

// 토큰 갱신 중복 방지
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

// TypeScript 타입 확장
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retryCount?: number;
  }
}

// 최대 재시도 횟수
const MAX_RETRY_COUNT = 3;

// Axios 인스턴스 생성
const axiosInstance = axios.create(DEFAULT_AXIOS_CONFIG);

// 요청 인터셉터 설정
axiosInstance.interceptors.request.use(
  (config) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.increaseLoading(); // T029: Use counter-based loading state

    const token = getRootStoreSingleton().authStore.getAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] =
      localStorage.getItem('i18nextLng') || i18next.language || 'ko';
    return config;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T031: Decrement counter on request error
    return Promise.reject(error);
  },
);

// 응답 인터셉터 설정
axiosInstance.interceptors.response.use(
  async (response) => {
    const data = response.data;
    const config = response.config;

    const authStore = getRootStoreSingleton().authStore;
    const snackbarStore = getRootStoreSingleton().snackbarStore;
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T030: Decrement counter on successful response

    if (data.resultCode === 'F') {
      if (
        (data.errorCode === '401' || data.errorCode === '4001') &&
        !config.url?.includes('/login/refresh') &&
        !config.url?.includes('/logout')
      ) {
        // 재시도 카운터 증가
        config._retryCount = (config._retryCount || 0) + 1;

        // 최대 재시도 횟수 초과 시 로그아웃 처리
        if (config._retryCount > MAX_RETRY_COUNT) {
          snackbarStore.setProps({
            open: true,
            category: Category.Error,
            title: i18next.t('common.commonError'),
          });
          authStore.initAuth();
          return response;
        }

        // 이미 다른 요청이 refresh 중이면 대기열에 등록
        if (isRefreshing) {
          return new Promise((resolve) => {
            addRefreshSubscriber((token: string | null) => {
              if (!token) {
                // refresh 실패 → 원래 응답 그대로 반환 (snackbar 중복 방지)
                resolve(response);
                return;
              }
              config.headers['Authorization'] = `Bearer ${token}`;
              config.headers['Accept-Language'] =
                localStorage.getItem('i18nextLng') || i18next.language || 'ko';
              resolve(axiosInstance(config));
            });
          });
        }

        // 첫 번째 401 요청이 refresh 담당
        isRefreshing = true;

        const refreshToken = authStore.getRefreshToken();
        if (refreshToken) {
          const result = await authStore.loginRefresh(refreshToken);
          if (result?.resultCode === 'S') {
            const oauthToken = result.data;
            if (oauthToken) {
              authStore.setAccessToken(oauthToken?.access_token);
              authStore.setRefreshToken(oauthToken?.refresh_token);
            }

            const newToken = authStore.getAccessToken() || '';
            isRefreshing = false;
            onRefreshed(newToken);

            config.headers['Authorization'] = `Bearer ${newToken}`;
            config.headers['Accept-Language'] =
              localStorage.getItem('i18nextLng') || i18next.language || 'ko';
            return axiosInstance(config);
          } else {
            // 토큰 갱신 실패: 서버 에러 메시지 대신 일반 메시지 사용 (보안)
            if (process.env.NODE_ENV === 'development' && result?.errorMessage) {
              // eslint-disable-next-line no-console
              console.warn('[Auth] Token refresh failed:', result.errorMessage);
            }
            isRefreshing = false;
            onRefreshed(null);
            snackbarStore.setProps({
              open: true,
              category: Category.Error,
              title: i18next.t('common.commonError'),
            });
            authStore.initAuth();
          }
        } else {
          isRefreshing = false;
          onRefreshed(null);
          authStore.initAuth();
        }
      } else if (data.errorCode !== '409') {
        // 비즈니스 에러는 서버 메시지 사용, 없으면 일반 메시지
        const serverMessage = data?.errorMessage || data?.err_msg;

        // 개발 환경에서만 상세 로그 출력
        if (process.env.NODE_ENV === 'development' && serverMessage) {
          // eslint-disable-next-line no-console
          console.warn('[API Error]', data.errorCode, serverMessage);
        }

        snackbarStore.setProps({
          open: true,
          category: Category.Error,
          title: serverMessage || i18next.t('common.commonError'),
        });
      }
    }

    return response;
  },
  (error) => {
    const loadingStore = getRootStoreSingleton().loadingStore;
    loadingStore.decreaseLoading(); // T031: Decrement counter on error response

    const snackbarStore = getRootStoreSingleton().snackbarStore;
    if (error.code === 'ERR_NETWORK') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.serverError'),
      });
    } else if (error.code === 'ECONNABORTED') {
      snackbarStore.setProps({
        open: true,
        category: Category.Error,
        title: i18next.t('common.commonError'),
      });
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
